import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, Clock, ArrowRight, Mail, Share2, Twitter, Facebook, Linkedin, Link as LinkIcon, Eye } from "lucide-react";
import SectionReveal from "@/components/SectionReveal";
import HeroNewsletterCard from "@/components/HeroNewsletterCard";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import useSeo from "@/hooks/useSeo";
import { toast } from "sonner";

function ShareRow({ url, title }) {
  const enc = encodeURIComponent;
  const tx = (txt) => `https://twitter.com/intent/tweet?text=${enc(txt)}&url=${enc(url)}`;
  const fb = (u) => `https://www.facebook.com/sharer/sharer.php?u=${enc(u)}`;
  const li = (u, t) => `https://www.linkedin.com/sharing/share-offsite/?url=${enc(u)}&title=${enc(t)}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy. Please copy manually.");
    }
  };
  return (
    <div className="flex items-center gap-3" data-testid="insight-share-row">
      <span className="text-xs tracking-wide uppercase text-ink-soft mr-1">Share</span>
      <a href={tx(title)} target="_blank" rel="noopener noreferrer" aria-label="Share on X / Twitter" className="w-9 h-9 inline-flex items-center justify-center rounded-full border border-hairline text-ink-soft hover:border-sage hover:text-sage transition" data-testid="insight-share-twitter">
        <Twitter size={14} strokeWidth={1.8} />
      </a>
      <a href={fb(url)} target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook" className="w-9 h-9 inline-flex items-center justify-center rounded-full border border-hairline text-ink-soft hover:border-sage hover:text-sage transition" data-testid="insight-share-facebook">
        <Facebook size={14} strokeWidth={1.8} />
      </a>
      <a href={li(url, title)} target="_blank" rel="noopener noreferrer" aria-label="Share on LinkedIn" className="w-9 h-9 inline-flex items-center justify-center rounded-full border border-hairline text-ink-soft hover:border-sage hover:text-sage transition" data-testid="insight-share-linkedin">
        <Linkedin size={14} strokeWidth={1.8} />
      </a>
      <button type="button" onClick={copy} aria-label="Copy link" className="w-9 h-9 inline-flex items-center justify-center rounded-full border border-hairline text-ink-soft hover:border-sage hover:text-sage transition" data-testid="insight-share-copy">
        <LinkIcon size={14} strokeWidth={1.8} />
      </button>
    </div>
  );
}

export default function InsightDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [isDraft, setIsDraft] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const canPreviewDrafts = user?.role === "admin" || user?.role === "editor";

  useSeo(
    article
      ? {
          title: `${article.title} · Pip Bennett`,
          description: article.meta_description || article.excerpt,
          canonical: `https://pipbennett.com/insights/${article.slug}`,
          ogType: "article",
          ogImage: article.featured_image || undefined,
          jsonLd: {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.meta_description || article.excerpt,
            author: { "@type": "Person", name: "Pip Bennett", url: "https://pipbennett.com/about" },
            publisher: { "@type": "Person", name: "Pip Bennett" },
            datePublished: article.published_at,
            dateModified: article.updated_at,
            mainEntityOfPage: `https://pipbennett.com/insights/${article.slug}`,
            image: article.featured_image ? [article.featured_image] : undefined,
            articleSection: article.category,
            keywords: (article.tags || []).join(", "),
          },
        }
      : { title: "Loading… · Pip Bennett" }
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setIsDraft(false);
    api
      .get(`/insights/${slug}${canPreviewDrafts ? "?preview=1" : ""}`)
      .then((r) => {
        if (cancelled) return;
        setArticle(r.data.article);
        setRelated(r.data.related || []);
        setIsDraft(!!r.data.is_draft);
      })
      .catch(() => { if (!cancelled) setNotFound(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    window.scrollTo({ top: 0, behavior: "instant" });
    return () => { cancelled = true; };
  }, [slug, canPreviewDrafts]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-ink-soft">
        Loading…
      </div>
    );
  }

  if (notFound || !article) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6" data-testid="insight-not-found">
        <div className="text-center max-w-md">
          <h1 className="font-serif-display text-4xl text-ink mb-4">Article not found.</h1>
          <p className="text-ink-soft mb-8">
            Maybe it was moved, maybe it never existed. Either way — head back to Insights.
          </p>
          <Link to="/insights" className="inline-flex items-center gap-2 text-sage font-medium">
            <ArrowLeft size={16} strokeWidth={1.8} /> Back to Insights
          </Link>
        </div>
      </div>
    );
  }

  const url = `https://pipbennett.com/insights/${article.slug}`;
  const publishedDate = article.published_at
    ? new Date(article.published_at).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })
    : "";

  return (
    <div data-testid={`page-insight-${article.slug}`}>
      {/* DRAFT PREVIEW BANNER — visible only to admin/editor viewing an
          unpublished draft. Gives an unmissable visual cue so preview URLs
          are never mistaken for live public content. */}
      {isDraft && (
        <div
          className="bg-gold text-ink py-2.5 px-4 text-center text-sm font-medium tracking-wide"
          data-testid="insight-draft-banner"
        >
          <Eye size={14} strokeWidth={2} className="inline mr-2 -mt-0.5" />
          Draft preview — visible only to you. Not published to the public yet.
        </div>
      )}
      {/* HEADER */}
      <section className="pt-10 md:pt-14 pb-8 md:pb-12 bg-gradient-to-b from-cream to-cream-soft">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <Link
            to="/insights"
            className="inline-flex items-center gap-1.5 text-sm text-ink-soft hover:text-sage transition mb-8"
            data-testid="insight-back-link"
          >
            <ArrowLeft size={14} strokeWidth={1.8} /> All Insights
          </Link>
          <SectionReveal>
            <div className="flex items-center gap-3 text-xs flex-wrap">
              <span className="eyebrow text-sage" data-testid="insight-category">{article.category}</span>
              <span className="text-ink-soft/40">·</span>
              <span className="inline-flex items-center gap-1 text-ink-soft">
                <Clock size={12} strokeWidth={1.8} /> {article.reading_time_minutes} min read
              </span>
              {publishedDate && (
                <>
                  <span className="text-ink-soft/40">·</span>
                  <time className="text-ink-soft" dateTime={article.published_at}>{publishedDate}</time>
                </>
              )}
            </div>
            <h1
              className="mt-5 font-serif-display text-4xl md:text-5xl lg:text-6xl text-ink leading-[1.05]"
              data-testid="insight-title"
            >
              {article.title}
            </h1>
            {article.subtitle && (
              <p className="mt-5 text-lg md:text-xl text-ink-soft italic leading-relaxed" data-testid="insight-subtitle">
                {article.subtitle}
              </p>
            )}
          </SectionReveal>
        </div>
      </section>

      {/* FEATURED IMAGE (optional) */}
      {article.featured_image && (
        <section className="pb-10">
          <div className="max-w-4xl mx-auto px-6 md:px-10">
            <img
              src={article.featured_image}
              alt=""
              className="w-full rounded-2xl object-cover aspect-[16/9]"
              data-testid="insight-featured-image"
              loading="eager"
            />
          </div>
        </section>
      )}

      {/* BODY */}
      <section className="pb-16 md:pb-20">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <article
            className="prose prose-lg prose-pip max-w-none"
            data-testid="insight-body"
          >
            <ReactMarkdown>{article.body || ""}</ReactMarkdown>
          </article>

          {/* TAGS */}
          {(article.tags || []).length > 0 && (
            <div className="mt-12 pt-8 border-t border-hairline flex flex-wrap gap-2" data-testid="insight-tags">
              {article.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/insights?tag=${encodeURIComponent(tag)}`}
                  className="text-xs tracking-wide text-ink-soft border border-hairline hover:border-sage hover:text-sage rounded-full px-3 py-1 transition"
                >
                  #{tag}
                </Link>
              ))}
            </div>
          )}

          {/* SHARE */}
          <div className="mt-8 pt-6 border-t border-hairline">
            <ShareRow url={url} title={article.title} />
          </div>
        </div>
      </section>

      {/* NEWSLETTER CTA */}
      <section className="py-16 md:py-20 bg-cream-soft border-y border-hairline">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
          <SectionReveal>
            <div className="inline-flex items-center gap-2 eyebrow text-sage mb-5">
              <Mail size={14} strokeWidth={1.8} />
              Sunday with Pip
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl text-ink leading-tight">
              If this resonated, you'll love what arrives on Sunday.
            </h2>
            <p className="mt-5 text-ink-soft leading-relaxed max-w-xl mx-auto">
              One quiet email each Sunday morning. An observation, a small invitation, a reminder
              that you're allowed to slow down. Nothing sold.
            </p>
            <div className="mt-8 max-w-lg mx-auto text-left">
              <HeroNewsletterCard />
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="py-16 md:py-24">
          <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-14">
            <SectionReveal>
              <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
                <h2 className="font-serif-display text-3xl md:text-4xl text-ink leading-tight">Keep reading.</h2>
                <Link to="/insights" className="text-sm text-sage hover:text-sage-dark font-medium inline-flex items-center gap-1">
                  All Insights <ArrowRight size={14} strokeWidth={1.8} />
                </Link>
              </div>
            </SectionReveal>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((r, i) => (
                <SectionReveal key={r.slug} delay={i * 80}>
                  <Link
                    to={`/insights/${r.slug}`}
                    className="group block bg-white border border-hairline rounded-2xl p-6 h-full transition hover:border-sage/40 hover:shadow-[0_15px_40px_-20px_rgba(74,93,79,0.15)]"
                    data-testid={`insight-related-${r.slug}`}
                  >
                    <div className="eyebrow text-sage mb-2">{r.category}</div>
                    <h3 className="font-serif-display text-xl text-ink leading-snug group-hover:text-sage transition">
                      {r.title}
                    </h3>
                    <p className="mt-3 text-sm text-ink-soft leading-relaxed line-clamp-3">{r.excerpt}</p>
                    <div className="mt-4 inline-flex items-center gap-1 text-xs text-ink-soft">
                      <Clock size={11} strokeWidth={1.8} /> {r.reading_time_minutes} min read
                    </div>
                  </Link>
                </SectionReveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
